import { Alert, FormButton, Button, Form, Input, message } from 'admin-web-ui'
import { Store } from 'rc-field-form/lib/interface'
import React, { useCallback } from 'react'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { ChangePasswordFields } from '../../components/Form'
import { ChangePasswordFieldsData } from '../../components/Form/ChangePasswordFields'
import { useAppContext } from '../../containers/App'
import { changePassword } from '../../utils/cognito'

const Footer = styled.div`
  margin-top: 8px;
`

const StyledSubmitButton = styled(FormButton.Submit)`
  width: 120px;
  height: 40px;
  margin: 0;
  margin-right: 15px;
`

export const CancelButton = styled(Button)`
  height: 40px;
  width: 120px;
`

interface FormData extends ChangePasswordFieldsData {
  oldPassword: string
}

interface Props {
  onSuccess: () => void
  onCancel: () => void
}

export const ChangePasswordForm: React.FC<Props> = (props) => {
  const { onSuccess, onCancel } = props
  const { cognitoUser } = useAppContext()
  if (!cognitoUser) {
    throw new Error(
      'Cognito user must be available in order to do global sign out.',
    )
  }

  const [submitState, submit] = useAsyncFn(
    async (store?: Store): Promise<void> => {
      if (!store) {
        return undefined
      }

      const data = store as FormData
      await changePassword({
        cognitoUser,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      })
      message.success('Your password has been updated.')
      onSuccess()
    },
    [cognitoUser, onSuccess],
  )

  const clearSubmitError = useCallback(() => {
    if (submitState.error) {
      submit(undefined)
    }
  }, [submitState, submit])

  return (
    <Form onFinish={submit} onFieldsChange={clearSubmitError}>
      <fieldset>
        <legend>Enter your current password</legend>
        <Form.Item
          name="oldPassword"
          className="change-password-form__old-password"
          validateStatus={submitState.error ? 'error' : undefined}
          rules={[
            { required: true, message: 'Please input your old password' },
          ]}
        >
          <Input.Password
            placeholder="Current password"
            autoComplete="current-password"
          />
        </Form.Item>
      </fieldset>

      <fieldset>
        <legend>Enter your new password</legend>
        <ChangePasswordFields submitFailure={!!submitState.error} />
      </fieldset>

      {submitState.error && (
        <Alert
          type="error"
          message={
            <>
              There was a problem changing your password. Please check your
              current password and try again.
            </>
          }
        />
      )}

      <Footer>
        <StyledSubmitButton
          className="automation-SaveButton"
          loading={submitState.loading}
        >
          Save
        </StyledSubmitButton>
        <CancelButton
          className="automation-CancelButton"
          type="default"
          shape="round"
          onClick={onCancel}
        >
          Cancel
        </CancelButton>
      </Footer>
    </Form>
  )
}
